import React, { useEffect } from 'react';
import { Text } from '@mantine/core';
import styles from './header.module.css';
import TopNavButton from './topNavButton/TopNavButton';
import { useSelector, useDispatch } from 'react-redux';
import {
  getUser,
  getUserLoading,
  getAuthError,
  getMemberships,
  canViewContinuingEducation,
} from '../../data/selectors/userAuthSelectors';
import PageNavButton from './pageNavButton/PageNavButton';
import { logoutUser, loadUser, clearSession, switchOrg } from '../../data/actions/userAuthActions';
import { Link, useNavigate, useLocation } from 'react-router-dom';

const AUTH_LOADING_TIMEOUT_MS = 5000;

const Header = () => {
  const dispatch = useDispatch();
  const user = useSelector(getUser);
  const userLoading = useSelector(getUserLoading);
  const authError = useSelector(getAuthError);
  const storeMemberships = useSelector(getMemberships);
  const memberships = storeMemberships?.length ? storeMemberships : user?.memberships || [];
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    dispatch(loadUser());
  }, []);

  // Auto-clear invalid session states (only when a session exists)
  useEffect(() => {
    if (authError && user.email) {
      dispatch(clearSession());
    }
  }, [authError, dispatch, user.email]);

  // Force logout if loadUser() hasn't resolved within the timeout
  useEffect(() => {
    if (!userLoading) return;
    const timeout = setTimeout(() => dispatch(clearSession()), AUTH_LOADING_TIMEOUT_MS);
    return () => clearTimeout(timeout);
  }, [userLoading, dispatch]);

  const handleLogout = () => {
    dispatch(logoutUser());
  };

  const handleOrgSwitch = ({ target }) => {
    const newOrgId = Number(target.value);
    if (!newOrgId || newOrgId === Number(user.organizations)) return;
    const res = dispatch(switchOrg(newOrgId));
    if (res && typeof res.then === 'function') {
      res.then((updatedUser) => {
        if (!updatedUser) return;
        if (
          location.pathname === '/org-admin' &&
          updatedUser.role !== 'org-admin' &&
          updatedUser.role !== 'know-admin'
        ) {
          navigate('/');
        } else if (
          location.pathname === '/continuing-education' &&
          !canViewContinuingEducation(updatedUser)
        ) {
          navigate('/');
        }
      });
    }
  };

  const mediaQuery = window.matchMedia('(max-width: 425px)');
  const userOnMobile = mediaQuery.matches;

  let pageNav;
  if (userLoading || !user.email || authError) {
    pageNav = [
      <PageNavButton key={'Home'} text={'KNOW Home'} link={'/'} />,
      <PageNavButton key={'Methodology'} text={'Methodology'} link={'/methodology'} />,
      <PageNavButton key={'Studies'} text={'Studies'} link={'/login#studies'} />,
      <PageNavButton key={'Chat'} text={'KNOW AI'} link={'/chat'} />,
      <PageNavButton key={'Provider Network'} text="Provider Network" link={'/providers'} />,
      <PageNavButton
        key={'Patient Resources'}
        text={'Patient Resources'}
        link={'/patient-resources'}
      />,
      <PageNavButton key={'About Us'} text={'About Us'} link={'/about-us'} />,
    ];
  } else {
    pageNav = [
      <PageNavButton key={'Home'} text={'KNOW Home'} link={'/'} />,
      <PageNavButton
        key={'Methodology'}
        text={userOnMobile ? 'Method-ology' : 'Methodology'}
        link={'/methodology'}
      />,
      <PageNavButton key={'Studies'} text={'Studies'} link={'/studies'} />,
      <PageNavButton key={'Chat'} text={'KNOW AI'} link={'/chat'} />,
      <PageNavButton key={'Provider Network'} text="Provider Network" link={'/providers'} />,
      // <PageNavButton key={'Pearls'} text={'Clinical Pearls'} link={'/pearls'} />,
      <PageNavButton
        key={'Patient Resources'}
        text={'Patient Resources'}
        link={'/patient-resources'}
      />,
      <PageNavButton
        key={'Continuing Education'}
        text={'Continuing Education'}
        link={'/continuing-education'}
      />,
      <PageNavButton key="Attributions" text={'About Us'} link={'/about-us'} />,
    ];

    if (!canViewContinuingEducation(user)) {
      pageNav = pageNav.filter((btn) => btn.key !== 'Continuing Education');
    }
  }

  let topNav;
  if (userLoading) {
    // While auth state is being determined, render no nav buttons to avoid
    // a layout shift when auth-dependent buttons appear after loading
    topNav = [];
  } else if (!user.email || authError) {
    topNav = [
      <TopNavButton key="donate" text={'Donate'} link="/donate" />,
      <TopNavButton key="join" text={'Sign Up'} link="/join-now" />,
      <TopNavButton key="login" text={'Log In'} link="/login" />,
    ];
  } else {
    topNav = [
      <TopNavButton key="donate" text={'Donate'} link="/donate" />,
      // <TopNavButton key='addPearl' text={'Add Pearl'} link="/add-pearl" />,
      <TopNavButton key="logout" text={'Log Out'} link="/" handleButtonClick={handleLogout} />,
    ];
    // if(user.role === 'student' || user.role === 'allied' || user.organizations !== 1) topNav.splice(1, 1);
    if (!userOnMobile && (user.role === 'org-admin' || user.role === 'know-admin')) {
      topNav.unshift(
        <TopNavButton
          className={styles.noMobile}
          key="orgAdmin"
          text="Org Admin"
          link="/org-admin"
        />
      );
    }
    if (!userOnMobile && user.role === 'know-admin') {
      topNav.unshift(
        <TopNavButton
          className={styles.noMobile}
          key="knowAdmin"
          text="Know Admin"
          link="/know-admin"
        />
      );
    }
    if (user.email === 'ccnm@ccnm.org') {
      topNav.splice(-1);
    }
    if (user.organizations !== 'ccnm' && Array.isArray(memberships) && memberships.length > 1) {
      topNav.unshift(
        <select
          key="orgSwitcher"
          aria-label="Active Organization"
          className={styles.orgSwitcher}
          value={user.organizations ?? ''}
          onChange={handleOrgSwitch}
        >
          {memberships.map((m) => {
            const suffix = m.isExpired ? ' (Expired)' : m.ssoBlocked ? ' (SSO required)' : '';
            return (
              <option
                key={m.organizationId}
                value={m.organizationId}
                disabled={Boolean(m.isExpired || m.ssoBlocked)}
              >
                {`${m.orgName}${suffix}`}
              </option>
            );
          })}
        </select>
      );
    }
  }

  // Only redirect to terms-of-use for valid users who haven't agreed yet
  if (
    user.email &&
    !authError &&
    user._id &&
    !user.agreed_to_terms &&
    location.pathname != '/terms-of-use'
  )
    navigate('/terms-of-use', { replace: true });

  return (
    <header>
      <div className={styles.nameTopNavContainer} role="header">
        <Link to="/" className={styles.titleContainer}>
          <div className={styles.title} title="link to home" role="link">
            <h1>KNOW</h1>
            <Text component="h3" className={styles.byline}>
              Knowledge in Integrative Oncology Website
            </Text>
          </div>
        </Link>
        {userOnMobile && (user.role === 'org-admin' || user.role === 'know-admin') && (
          <p className={styles.mobileAdminWarning}>
            Admin pages are not available on mobile devices.
          </p>
        )}
        <nav className={styles.topNav} aria-label="user navigation">
          {topNav}
        </nav>
      </div>

      <div id="logo"></div>

      {pageNav.length === 7 ? (
        <nav className={`${styles.pageNav} ${styles.pageNavFull}`} aria-label="page navigation">
          {pageNav}
        </nav>
      ) : (
        <nav className={styles.pageNav} aria-label="page navigation">
          {pageNav}
        </nav>
      )}
    </header>
  );
};

export default Header;
